<script>
import OrderGeneralInfo from "./order-parts/OrderGeneralInfo.vue";
import OrderAudioNotes from "./order-parts/OrderAudioNotes.vue";
import OrderActors from "./order-parts/OrderActors.vue";
import OrderAttachments from "./order-parts/OrderAttachments.vue";
import FilmCutsEditor from "@/components/parts/FilmCutsEditor.vue";
import helpers from "../../.././helpers.js";
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { log } from "@/utils/clientLogger.js";
import $http from "@/axios.config";
import Swal from "sweetalert2";

export default {
  name: "OrderForm",
  components: {
    FilmCutsEditor,
    OrderAudioNotes,
    OrderGeneralInfo,
    OrderActors,
    OrderAttachments
  },
  emits: ['submitOrder', 'orderLoaded', 'cutsLogged'],
  props: {
    orderId: String,
    hideAttachmentsUpload: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    return { t, router };
  },
  data() {
    return {
      isSubmitting: false,
      cutsSaveState: '',
      currentPatternFiles: [],
      filesToDeleteOnSave: [],
      isDemoCollapsed: true,
      orderData: {
        title: "",
        description: "",
        status: "active",
        isUrgent: false,
        clientId: "",
        managerId: "",
        draftsmanId: "",
        badgeId: null,
        imagesId: [],
        filesId: [],
        filmCuts: [],
        filmWidth: 0,
        filmHeight: 0,
        filmSize: 0,
        carBrand: "",
        carModel: "",
        carYear: "",
        main_note: "",
        notes_gallery:  [],
        isModelUnknown: false,
        carImageUrl: "",
        hasProblematicPattern: false,
        hasProblematicPackaging: false,
        wasCut: false,
        hasNoPatterns: false,
        patternRequestSent: false,
        noPatternsType: '',
        assemblyPrograms: [],
      },
      brandsList: [],
      modelsList: [],
      yearsList: [],
      selected: null,
      activeDropZone: null,
      isUpdated: false,
      addClient: false,
      value: {},
      tempClients: [],
      tempOrders: [],
      tempUsers: [],
      uploadUrl: '/api/upload',
      fileRecords: [],
      imageRecords: [],
      uploadHeaders: { 'Authorization': this.$store.getters.userToken },
      fileRecordsForUpload: [],
      clientData: {},
      managerData: {},
      updateData: {},
      oldClientData: {},
      oldManagerData: {},
      oldImageRecords: [],
      oldFileRecords: [],
      oldOrderData: {},
      isRecording: false,
      mediaRecorder: null,
      audioChunks: [],
      mediaStream: null,
      isAudioCollapseOpen: true,
      badges: {},
      patternsModal: false,
      patternsItems: [],
      selectedPatternItem: null,
      patternsParts: [],
      loadingPatterns: false
    }
  },
  beforeUnmount() {
    document.removeEventListener('paste', this.handlePaste);

    if (this.isRecording || this.mediaStream) {
      log.warn('View:OrderForm:beforeUnmount', { 'message': 'Component destroyed, forced recording stop.' });

      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
      }

      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop());
      }

      this.isRecording = false;
      this.audioChunks = [];
      this.mediaRecorder = null;
      this.mediaStream = null;
    }

    if (this.$socket) {
      this.$socket.off('audio:was_read', this.handleAudioWasRead);

      if (this.orderId) {
        this.$socket.emit('leaveRoom', { roomId: this.orderId });
        log.info('View:OrderForm:Socket', { message: 'Sent leaveRoom', orderId: this.orderId });
      }
    }
  },
  watch: {
    'orderData.filmCuts': {
      handler() {
        this.syncAssemblyProgramsFromCuts();
      },
      deep: true
    }
  },
  methods: {
    handleCardClick(event, patternId) {
      if (event.target.closest('[data-fancybox]')) {
        return;
      }
      this.fetchPatternParts(patternId);
    },
    handleUpdateData({ field, value }) {
      this.orderData[field] = value;
    },
    handleSelectOrder(order) {
      this.orderData.title = order.title;
      this.tempOrders = [];
    },
    toggleAudioCollapse() {
      this.isAudioCollapseOpen = !this.isAudioCollapseOpen;
      localStorage.setItem(`audioCollapseState_${this.orderId}`, JSON.stringify(this.isAudioCollapseOpen));
    },
    submissionComplete() {
      this.isSubmitting = false;
    },
    async handlePaste(event) {
      const items = (event.clipboardData || event.originalEvent.clipboardData).items;
      for (let index in items) {
        const item = items[index];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file && file.type.startsWith('image/')) {
            const formData = new FormData();
            formData.append('file', file);

            try {
              const response = await this.$http.post('/api/upload', formData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                  'Authorization': this.$store.getters.userToken
                }
              });

              const uploaded = response.data;
              const safeSize = Number(uploaded.size) || 0;
              const record = {
                id: uploaded._id,
                type: uploaded.type,
                name: uploaded.fileName,
                url: uploaded._id ? '/api/download/' + uploaded._id : '',
                size: safeSize,
                sizeText: this.formatFileSize(safeSize),
                meta: {
                  name: uploaded.fileName,
                  size: safeSize,
                  type: uploaded.type
                }
              };

              if (this.activeDropZone === 'carPhotos') {
                this.imageRecords.push(record);
                this.mapChanges("image", this.t('messages.uploadedImages', { count: 1 }));
              } else if (this.activeDropZone === 'orderFiles') {
                this.fileRecords.push(record);
                this.mapChanges("file", this.t('messages.uploadedFiles', { count: 1 }));
              } else {
                log.warn('View:OrderForm:handlePaste', {
                  'message': 'Insert without active area - file ignored'
                });
              }
            } catch (error) {
              log.error('View:OrderForm:handlePaste', {
                'error': error,
                'message': 'Error loading image'
              });
              this.$swal.fire({
                icon: 'error',
                title: this.t('alerts.error'),
                text: this.t('messages.fileNotUploaded')
              });
            }
          }
        }
      }
    },
    resetForm() {
      this.resetOrderData();
      this.clientData = {};
      this.managerData = {};
      this.fileRecords = [];
      this.imageRecords = [];
      this.tempClients = [];
      this.tempUsers = [];
      this.tempOrders = [];
      this.addClient = false;
    },

    async fetchBrands() {
      try {
        const res = await this.$http.get('/api/car-data', { params: { action: 'brands' } });
        this.brandsList = res.data.brands || [];
      } catch (error) {
        console.error("Помилка завантаження марок:", error);
      }
    },

    async fetchModels() {
      this.orderData.carModel = '';
      this.orderData.carYear = '';
      this.modelsList = [];
      this.yearsList = [];

      if (!this.orderData.carBrand) return;

      try {
        const res = await this.$http.get('/api/car-data', {
          params: { action: 'models', brand: this.orderData.carBrand }
        });
        this.modelsList = res.data.models || [];
      } catch (error) {
        console.error("Помилка завантаження моделей:", error);
      }
    },

    async fetchYears() {
      this.orderData.carYear = '';
      this.yearsList = [];

      if (!this.orderData.carModel) return;

      try {
        const res = await this.$http.get('/api/car-data', {
          params: { action: 'years', brand: this.orderData.carBrand, model: this.orderData.carModel }
        });
        this.yearsList = res.data.years || [];
      } catch (error) {
        console.error("Помилка завантаження років:", error);
      }
    },

    async initCarData() {
      await this.fetchBrands();

      if (this.orderId && this.orderData.carBrand) {
        const resModels = await this.$http.get('/api/car-data', {
          params: { action: 'models', brand: this.orderData.carBrand }
        });
        this.modelsList = resModels.data.models || [];

        if (this.orderData.carModel) {
          const resYears = await this.$http.get('/api/car-data', {
            params: { action: 'years', brand: this.orderData.carBrand, model: this.orderData.carModel }
          });
          this.yearsList = resYears.data.years || [];
        }
      }
    },

    async fetchPatternsItems() {
      this.patternsModal = true;
      this.loadingPatterns = true;
      this.selectedPatternItem = null;

      try {
        const res = await this.$http.get('/api/car-data', {
          params: {
            action: 'items',
            brand: this.orderData.carBrand,
            model: this.orderData.carModel,
            year: this.orderData.carYear
          }
        });
        this.patternsItems = res.data.patterns || [];
      } catch (error) {
        console.error("Помилка:", error);
      } finally {
        this.loadingPatterns = false;
      }
    },

    async fetchPatternParts(patternOrId) {
      const itemId = typeof patternOrId === 'object' ? patternOrId.id_pattern : patternOrId;

      if (!itemId) return;

      if (this.patternsItems && this.patternsItems.length > 0) {
        const selectedPattern = this.patternsItems.find(p => p.id_pattern === itemId);
        if (selectedPattern && selectedPattern.photo) {
          this.orderData.carImageUrl = selectedPattern.photo.startsWith('http')
              ? selectedPattern.photo
              : 'https://patterns.brawix.com' + selectedPattern.photo;
        }
      }

      this.loadingPatterns = true;

      try {
        const res = await this.$http.get('/api/car-data', {
          params: { action: 'parts', item: itemId }
        });

        let partsData = res.data.data || [];

        this.currentPatternFiles = partsData.files || [];

        this.orderData.main_note = partsData.note;
        this.orderData.notes_gallery = partsData.notes_gallery || [];

        if (partsData.packs) {
          partsData.packs = partsData.packs.map((pack) => {
            const hasSelectedCuts = pack.cuts.some(cut => this.isCutSelected(cut.id));
            pack.collapsed = !hasSelectedCuts;
            return pack;
          });
        }

        this.patternsParts = partsData;
        this.selectedPatternItem = itemId;
      } catch (error) {
        console.error("Помилка деталей:", error);
      } finally {
        this.loadingPatterns = false;
      }
    },

    async addPartToOrder(cut) {
      let currentCuts = [...(this.orderData.filmCuts || [])];

      if (currentCuts.length > 0 && currentCuts[0].patternId !== this.selectedPatternItem) {

        const confirmChange = await this.$swal.fire({
          title: this.t('asks.warning', 'Зміна автомобіля'),
          text: this.t('messages.carChangedDeletePatterns'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: this.t('buttons.confirm'),
          cancelButtonText: this.t('buttons.cancel')
        });

        if (!confirmChange.isConfirmed) return;

        currentCuts = [];

        const oldPatternsToDelete = this.fileRecords.filter(f => {
          const fileName = (f.name || "").toUpperCase();
          return f.isPatternFile || fileName.endsWith('.MMM') || fileName.endsWith('.CWE') || fileName.endsWith('.BAK');
        });

        oldPatternsToDelete.forEach(file => {
          if (file.id && !String(file.id).startsWith('pattern_')) {
            this.filesToDeleteOnSave.push(file.id);
          }
        });

        this.fileRecords = this.fileRecords.filter(f => !oldPatternsToDelete.includes(f));
      }

      if (this.currentPatternFiles && this.currentPatternFiles.length > 0) {
        this.currentPatternFiles.forEach(file => {

          const alreadyExists = this.fileRecords.some(f => f.originalPatternName === file.name || f.name === file.name);

          if (!alreadyExists) {
            const uniqueString = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9);
            const frontendId = 'pattern_' + uniqueString;

            this.fileRecords.push({
              id: frontendId,
              name: file.name,
              originalPatternName: file.name,
              url: file.url,
              type: 'application/octet-stream',
              size: 0,
              sizeText: 'Pattern File',
              isPatternFile: true
            });
          }
        });
      }

      const existsIndex = currentCuts.findIndex(item => item.id === cut.id);

      if (existsIndex === -1) {
        currentCuts.push({
          id: cut.id,
          patternId: this.selectedPatternItem,
          name: cut.name || `${this.t('labels.pattern')} #${cut.id}`,
          area: cut.area || 0,
          width: cut.width || 0,
          height: cut.height || 0,
          note: cut.note || "",
          path: cut.image || cut.url || "",
          image: cut.image || cut.url || "",
          url: cut.image || cut.url || ""
        });
      } else {
        currentCuts.splice(existsIndex, 1);
      }

      this.orderData.filmCuts = currentCuts;
      this.autoSavePatternSelection();
    },

    onCutsChange() {
      this.mapChanges('cuts', this.orderData.filmCuts);
    },

    onCutProgramChange({ cutId, program, oldProgram }) {
      if (!this.orderId || !cutId) return;

      const cuts = this.orderData.filmCuts || [];
      let programs = Array.isArray(this.orderData.assemblyPrograms) ? [...this.orderData.assemblyPrograms] : [];
      if (oldProgram && oldProgram !== program && !cuts.some(c => c.program === oldProgram)) {
        programs = programs.filter(p => p !== oldProgram);
      }
      if (program && !programs.includes(program)) {
        programs.push(program);
      }
      this.orderData.assemblyPrograms = programs;

      this.cutsSaveState = 'saving';
      this.$http.put(`/api/orders/${this.orderId}/cut-program`, { cutId, program })
        .then(() => this.markCutsSaved())
        .catch((error) => {
          this.cutsSaveState = 'error';
          log.error('View:OrderForm:onCutProgramChange', { error, orderId: this.orderId });
        });
    },

    onCutRemoved(imageId) {
      if (imageId && !this.filesToDeleteOnSave.includes(imageId)) {
        this.filesToDeleteOnSave.push(imageId);
      }
    },

    syncAssemblyProgramsFromCuts() {
      const cutPrograms = (this.orderData.filmCuts || [])
          .map(cut => cut.program)
          .filter(Boolean);
      if (cutPrograms.length === 0) return;

      const current = Array.isArray(this.orderData.assemblyPrograms) ? this.orderData.assemblyPrograms : [];
      const merged = [...new Set([...current, ...cutPrograms])];

      if (merged.length !== current.length) {
        this.orderData.assemblyPrograms = merged;
      }
    },

    formatCutsForLog(cuts) {
      return (cuts || []).map((cut, index) => {
        let details = `#${index + 1} (${cut.width || 0}x${cut.height || 0}м)`;
        if (cut.note) {
          details += ` - ${this.t('log.notePrefix', 'Примітка')}: ${cut.note}`;
        }
        return details;
      }).join('\n');
    },

    buildCutsLog() {
      const before = this.formatCutsForLog(this._cutsBaseline) || this.t('log.noItems', 'Не було');
      const after = this.formatCutsForLog(this.orderData.filmCuts);

      if (before === after) return null;

      return {
        before,
        after: after || this.t('log.allCutsDeleted', 'Видалено всі викрійки')
      };
    },

    afterCutsSaved(cutsLog) {
      if (cutsLog) {
        this._cutsBaseline = JSON.parse(JSON.stringify(this.orderData.filmCuts || []));
        this.$emit('cutsLogged');
      }
      this.markCutsSaved();
    },

    async uploadPendingPatternFiles() {
      const pendingPatternFiles = this.fileRecords.filter(f => f.isPatternFile && String(f.id).startsWith('pattern_'));
      if (pendingPatternFiles.length === 0) return;

      const uploadPromises = pendingPatternFiles.map(async (file) => {
        try {
          const uploadRes = await this.$http.post('/api/upload-external', {
            url: file.url,
            fileName: file.name
          }, {
            headers: {
              'Authorization': this.$store.getters.userToken
            }
          });
          return { file, data: uploadRes.data };
        } catch (err) {
          log.error('View:OrderForm:registerPatternFiles', { message: `Error registering pattern file: ${file.name}`, error: err });
          return null;
        }
      });

      const results = await Promise.all(uploadPromises);

      results.forEach(result => {
        if (result && result.data && result.data._id) {
          const newId = String(result.data._id).trim();
          const fileIndex = this.fileRecords.findIndex(f => f.id === result.file.id);
          if (fileIndex !== -1) {
            this.fileRecords[fileIndex].id = newId;
            this.fileRecords[fileIndex].size = result.data.size || result.file.size;
            this.fileRecords[fileIndex].sizeText = this.t('labels.pattern', 'Файл лекала');
          }
        }
      });
    },

    markCutsSaved() {
      this.cutsSaveState = 'saved';
      clearTimeout(this._cutsSavedHideTimer);
      this._cutsSavedHideTimer = setTimeout(() => {
        if (this.cutsSaveState === 'saved') this.cutsSaveState = '';
      }, 2500);
    },

    autoSaveCuts() {
      if (!this.orderId) return;

      clearTimeout(this._cutsSaveTimer);
      this.cutsSaveState = 'saving';

      this._cutsSaveTimer = setTimeout(() => {
        const cutsLog = this.buildCutsLog();
        this.$http.put(`/api/orders/cuts/${this.orderId}`, {
            filmCuts: this.orderData.filmCuts || [],
            assemblyPrograms: this.orderData.assemblyPrograms || [],
            cutsLog
          })
          .then(() => this.afterCutsSaved(cutsLog))
          .catch((error) => {
            this.cutsSaveState = 'error';
            log.error('View:OrderForm:autoSaveCuts', { error, orderId: this.orderId });
          });
      }, 500);
    },

    autoSavePatternSelection() {
      if (!this.orderId) return;

      clearTimeout(this._cutsSaveTimer);
      this.cutsSaveState = 'saving';

      this._cutsSaveTimer = setTimeout(async () => {
        try {
          if (this.filesToDeleteOnSave && this.filesToDeleteOnSave.length > 0) {
            for (const fileId of this.filesToDeleteOnSave) {
              try {
                await this.$http.delete(`/api/upload/${fileId}`);
              } catch (err) {
                log.error('View:OrderForm:autoSavePatternSelection:delete', { fileId, error: err });
              }
            }
            this.filesToDeleteOnSave = [];
          }

          await this.uploadPendingPatternFiles();
          this.getFiles();

          const isValidObjectId = (id) => typeof id === 'string' && id.length === 24;

          const cutsLog = this.buildCutsLog();
          await this.$http.put(`/api/orders/cuts/${this.orderId}`, {
            filmCuts: this.orderData.filmCuts || [],
            filesId: (this.orderData.filesId || []).filter(isValidObjectId),
            assemblyPrograms: this.orderData.assemblyPrograms || [],
            cutsLog
          });

          this.afterCutsSaved(cutsLog);
        } catch (error) {
          this.cutsSaveState = 'error';
          log.error('View:OrderForm:autoSavePatternSelection', { error, orderId: this.orderId });
        }
      }, 500);
    },

    isCutSelected(cutId) {
      if (!this.orderData.filmCuts) return false;
      return this.orderData.filmCuts.some(item => item.id === cutId);
    },

    openPatternsModal() {
      this.patternsModal = true;

      if (this.selectedPatternItem && this.patternsParts && this.patternsParts.packs) {
        return;
      }

      if (this.orderData.filmCuts && this.orderData.filmCuts.length > 0 && this.orderData.filmCuts[0].patternId) {
        const savedPatternId = this.orderData.filmCuts[0].patternId;
        this.fetchPatternParts(savedPatternId);
      }
      else {
        this.fetchPatternsItems();
      }
    },

    setActiveZone(zone) {
      this.activeDropZone = zone;
    },
    clearActiveZone() {
      this.activeDropZone = null;
    },
    handleAudioUploaded(uploadedFile) {
      const record = {
        id: uploadedFile._id,
        type: uploadedFile.type,
        name: uploadedFile.fileName,
        url: this.formatSafeUrl(uploadedFile.path),
        size: uploadedFile.size || 0,
        sizeText: this.formatFileSize(uploadedFile.size || 0),
        createdAt: uploadedFile.createdAt || new Date().toISOString(),
        uploader: this.$store.getters.userData,
        file: { name: uploadedFile.fileName, type: uploadedFile.type, size: uploadedFile.size || 0 },
        meta: { name: uploadedFile.fileName, size: uploadedFile.size || 0, type: uploadedFile.type }
      };

      this.fileRecords.push(record);

      if (this.$socket) {
        this.$socket.emit('notification:new_audio_note', {
          orderId: this.orderId,
          fileName: record.name,
          uploaderName: this.$store.getters.userData.username || this.$store.getters.userData.login
        });
      }

      this.mapChanges("file_add", { name: uploadedFile.fileName });
      this.mapChanges("file", this.t('messages.uploadedFiles', { count: 1 }));
    },
    markAudioAsRead(audio) {
      const currentUserId = this.$store.getters.userData._id;
      const alreadyRead = audio.readBy && audio.readBy.find(user => user._id === currentUserId);

      if (alreadyRead) {
        return;
      }

      if (this.$socket) {
        this.$socket.emit('audio:read', {
          fileId: audio.id,
          orderId: this.orderId
        });
      } else {
        log.error('View:OrderForm:markAudioAsRead', { 'error': 'Socket not available!' });
      }
    },
    setCuttingDone() {
      if (!this.orderId) return;

      this.$swal.fire({
        title: this.t('asks.confirmCuttingDoneTitle'),
        text: this.t('asks.confirmCuttingDoneText'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.t('buttons.confirm'),
        cancelButtonText: this.t('buttons.cancel')
      }).then(async (result) => {
        if (result.isConfirmed) {
          this.isSubmitting = true;
          this.$http.put(`/api/orders/${this.orderId}/set-cutting-done`, { wasCut: true })
              .then(() => {
                this.orderData.status = 'done';
                this.orderData.wasCut = true;
                this.oldOrderData.status = 'done';
                this.oldOrderData.wasCut = true;
                this.$swal.fire(this.t('alerts.success'), this.t('messages.orderCuttingDone'), 'success').then(() => {
                  this.router.push('/');
                });
              })
              .catch((e) => {
                console.error(e);
                this.$swal.fire(this.t('alerts.error'), e.response?.data?.message, 'error');
              })
              .finally(() => {
                this.isSubmitting = false;
              });
        }
      });
    },
    sendToCutting() {
      if (!this.orderId) return;

      this.$swal.fire({
        title: this.t('asks.confirmCuttingTitle'),
        text: this.t('asks.confirmCuttingText'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.t('buttons.confirm'),
        cancelButtonText: this.t('buttons.cancel')
      }).then((result) => {
        if (result.isConfirmed) {
          this.isSubmitting = true;
          this.$http.put(`/api/orders/${this.orderId}/set-cutting`)
              .then(() => {
                this.orderData.status = 'cutting';
                this.oldOrderData.status = 'cutting';
                this.$swal.fire(this.t('alerts.success'), this.t('messages.orderSentToCutting'), 'success');
              })
              .catch((e) => {
                console.error(e);
                this.$swal.fire(this.t('alerts.error'), e.response?.data?.message, 'error');
              })
              .finally(() => {
                this.isSubmitting = false;
              });
        }
      });
    },
    async submitOrder() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;

      if (this.orderData.isManualTitle) {
        if (!this.orderData.title) {
          this.$swal.fire({
            icon: 'warning',
            title: this.t('alerts.warning'),
            text: this.t('placeholders.carBrandModel')
          });
          this.isSubmitting = false;
          return;
        }
        this.orderData.isModelUnknown = false;
      } else {
        if (!this.orderData.isModelUnknown && (!this.orderData.carBrand || !this.orderData.carModel || !this.orderData.carYear)) {
          this.$swal.fire({
            icon: 'warning',
            title: this.t('alerts.warning'),
            text: this.t('messages.selectModelOrCheckUnknown')
          });
          this.isSubmitting = false;
          return;
        }
      }

      if (this.orderData.hasNoPatterns && !this.orderData.noPatternsType) {
        this.$swal.fire({
          icon: 'warning',
          title: this.t('alerts.warning'),
          text: this.t('messages.selectNoPatternsStatus', 'Будь ласка, оберіть статус відсутності лекал.')
        });
        this.isSubmitting = false;
        return;
      }

      if (this.oldOrderData.status === 'cutting' && this.orderData.status === 'done' && !this.orderData.wasCut) {
        const confirmReminder = await this.$swal.fire({
          title: this.t('asks.warning'),
          text: this.t('asks.confirmWasCutReminder'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: this.t('buttons.confirm'),
          cancelButtonText: this.t('buttons.cancel')
        });

        if (!confirmReminder.isConfirmed) {
          this.isSubmitting = false;
          return;
        }
      }

      try {
        if (this.filesToDeleteOnSave && this.filesToDeleteOnSave.length > 0) {
          for (const fileId of this.filesToDeleteOnSave) {
            try {
              await this.$http.delete(`/api/upload/${fileId}`);
            } catch (err) {
              log.error('View:OrderForm:submitOrder', { message: `Не вдалося видалити старий файл ${fileId}`, error: err });
            }
          }
          this.filesToDeleteOnSave = [];
        }

        await this.uploadPendingPatternFiles();

        this.getFiles();

        this.orderData.managerId = this.orderManager;
        this.orderData.clientId = this.clientData?._id;

        if (this.orderId) {
          this.orderData._id = this.orderId;
        }

        const isValidObjectId = (id) => typeof id === 'string' && id.length === 24;

        const cleanOrder = {
          ...this.orderData,
          imagesId: this.orderData.imagesId.filter(isValidObjectId),
          filesId: this.orderData.filesId.filter(isValidObjectId),
        };

        this.$emit('submitOrder', cleanOrder);

        const hasChanges = Object.keys(this.updateData).length > 0;
        if (hasChanges && this.orderId) {
          const changes = [];
          const oldCar = `${this.oldOrderData.carBrand || ''} ${this.oldOrderData.carModel || ''} ${this.oldOrderData.carYear || ''}`.trim();
          const newCar = `${this.orderData.carBrand || ''} ${this.orderData.carModel || ''} ${this.orderData.carYear || ''}`.trim();

          if (oldCar !== newCar) {
            changes.push({ field: 'car', before: oldCar || '—', after: newCar || '—' });
          }

          Object.entries(this.updateData).forEach(([field, diff]) => {
            const ignoredFields = ['imageEvents', 'fileEvents', 'image', 'file', 'oldImageNames', 'oldFileNames'];
            if (ignoredFields.includes(field)) return;

            let beforeVal = typeof diff === 'object' ? diff.before : '—';
            let afterVal = typeof diff === 'object' ? diff.after : diff;

            if (field === 'status') {
              beforeVal = this.oldOrderData.status || '—';
              afterVal = this.orderData.status; // Тут гарантовано лежить системний ключ (наприклад, 'work')
            }

            changes.push({
              field: field,
              before: (beforeVal === undefined || beforeVal === null || beforeVal === '') ? '—' : beforeVal,
              after: (afterVal === undefined || afterVal === null || afterVal === '') ? '—' : afterVal,
            });
          });

          const beforeImageNames = this.oldImageRecords.map(img => img.name).join(', ') || '—';
          const afterImageNames = this.imageRecords.map(img => img.name).join(', ') || '—';
          if (beforeImageNames !== afterImageNames) {
            changes.push({ field: 'image', before: beforeImageNames, after: afterImageNames });
          }

          const beforeFileNames = this.oldFileRecords.map(f => f.name).join(', ') || '—';
          const afterFileNames = this.fileRecords.map(f => f.name).join(', ') || '—';
          if (beforeFileNames !== afterFileNames) {
            changes.push({ field: 'file', before: beforeFileNames, after: afterFileNames });
          }

          if (changes.length > 0) {
            const logEntry = { type: 'edit_order', orderId: this.orderId, description: JSON.stringify(changes), createdAt: new Date() };
            try {
              await this.$http.post('/api/operations', [logEntry]);
            } catch (err) {
              log.error('View:OrderForm:submitOrder', {'error': err?.response?.data || err.message});
            }
          }
        }

        this.updateData = {};
        this.oldOrderData = JSON.parse(JSON.stringify(this.orderData));
        if (this.clientData) this.oldClientData = JSON.parse(JSON.stringify(this.clientData));
        if (this.managerData) this.oldManagerData = JSON.parse(JSON.stringify(this.managerData));
        this.oldImageRecords = JSON.parse(JSON.stringify(this.imageRecords));
        this.oldFileRecords = JSON.parse(JSON.stringify(this.fileRecords));

      } catch (mainError) {
        console.error("Критична помилка при збереженні замовлення:", mainError);
        this.isSubmitting = false;
      }
    },

    async asyncFind(query) {
      try {
        const params = {
          name: query,
          limit: 50
        };

        const clients = await this.$http.get('/api/clients/', { params });

        if (clients.data && Array.isArray(clients.data.clientData)) {
          this.tempClients = clients.data.clientData.slice(0, 50);
        } else {
          this.tempClients = [];
        }
      } catch (e) {
        log.error('View:OrderForm:asyncFind', {'error': e});
        this.tempClients = [];
      }
    },
    async asyncFindOrders(query) {
      if (!query || query.length < 1) return;
      const orders = await this.$http.get('/api/orders/', {params: {filter: {title: query}}});
      this.tempOrders = orders.data.ordersData;
    },
    async asyncFindUsers(query) {
      try {
        const params = {
          type: "manager",
          name: query,
          limit: 50
        };

        const users = await this.$http.get('/api/users/', { params });

        if (users.data && Array.isArray(users.data.data)) {
          this.tempUsers = users.data.data.slice(0, 50);
        } else {
          this.tempUsers = [];
        }
      } catch (e) {
        log.error('View:OrderForm:asyncFindUsers', {'error': e});
        this.tempUsers = [];
      }
    },
    getFiles() {
      const isValidObjectId = (id) => typeof id === 'string' && id.length === 24;

      this.orderData.filesId = this.fileRecords
          .map(file => file.id)
          .filter(id => isValidObjectId(id));

      this.orderData.imagesId = this.imageRecords
          .map(file => file.id)
          .filter(id => isValidObjectId(id));
    },
    imageFilesUpload(fileRecordsNewlySelected) {
      fileRecordsNewlySelected.forEach(file => {
        const safeSize = Number(file.data.size) || 0;
        this.imageRecords.push({
          id: file.data._id,
          type: file.data.type,
          name: file.data.fileName,
          url: file.data._id ? '/api/download/' + file.data._id : '',
          size: safeSize,
          sizeText: this.formatFileSize(safeSize),
          meta: {
            name: file.data.fileName,
            size: safeSize,
            type: file.data.type
          },
          isPatternFile: false
        });
        this.mapChanges("image_add", {name: file.data.fileName});
      });
      this.getFiles();
      this.mapChanges("image", this.t('messages.uploadedImages', {count: fileRecordsNewlySelected.length}));
    },

    filesRecordsUpload(fileRecordsNewlySelected) {
      fileRecordsNewlySelected.forEach(file => {
        const fileName = file.data.fileName || "";
        const ext = fileName.split('.').pop().toUpperCase();
        const isPattern = ['CWE', 'MMM', 'BAK', 'RAR', 'ZIP', '7Z'].includes(ext);

        this.fileRecords.push({
          id: file.data._id,
          type: file.data.type,
          name: fileName,
          url: file.data._id ? '/api/download/' + file.data._id : '',
          size: typeof file.data.size === 'number' ? file.data.size : 0,
          sizeText: isPattern ? this.t('labels.pattern', 'Файл лекала') : this.formatFileSize(file.data.size),
          isPatternFile: isPattern
        });
        this.mapChanges("file_add", {name: fileName});
      });
      this.getFiles();
      this.mapChanges("file", this.t('messages.uploadedFiles', {count: fileRecordsNewlySelected.length}));
    },
    onBeforeDelete(fileRecord, type = 'image') {
      const id = fileRecord.raw ? fileRecord.raw.id : fileRecord.id;
      if (!id) return;

      const name = fileRecord.meta?.name || fileRecord.file?.name || fileRecord.name || 'unnamed';

      this.$swal.fire({
        title: this.t('alerts.warning'),
        text: this.t('asks.delete_file'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.t('buttons.delete'),
        cancelButtonText: this.t('buttons.cancel')
      }).then(result => {
        if (result.isConfirmed) {
          const collection = type === 'image' ? this.imageRecords : this.fileRecords;
          const url = "/api/upload/" + id;

          const index = collection.findIndex(item => item.id === id);
          if (type === 'image') {
            this.updateData.oldImageNames = this.imageRecords.map(img => img.name);
            this.mapChanges('image_delete', {
              name: name,
              index
            });
          } else {
            if (!this.updateData.oldFileNames) {
              this.updateData.oldFileNames = this.fileRecords.map(f => f.name);
            }
            this.mapChanges('file_delete', {
              name: name,
              index
            });
          }

          if (type === 'image') {
            this.imageRecords = this.imageRecords.filter(item => item.id !== id);
          } else {
            this.fileRecords = this.fileRecords.filter(item => item.id !== id);
          }

          this.$http.delete(url).then(r => {
            this.$swal.fire({
              icon: 'success',
              title: this.t('alerts.success'),
              text: helpers.customReplace("{name}", r.data.fileName, this.t('messages.fileDeleted'))
            })
          }).catch(err => {
            this.$swal.fire({
              icon: 'error',
              title: this.t('alerts.error'),
              text: err?.response?.data?.message
            })
          });
        } else {
          if (type === 'image') {
            this.$refs.carPhotosRef?.cancelDelete(fileRecord);
          } else {
            this.$refs.orderFilesRef?.cancelDelete(fileRecord);
          }
        }
      })
    },
    customDelete(fileRecord) {
      return fileRecord;
    },
    resetOrderData() {
      this.orderData = {
        title: "",
        description: "",
        status: "active",
        isUrgent: false,
        clientId: "",
        managerId: this.$store.getters.userData._id,
        draftsmanId: "",
        badgeId: null,
        imagesId: [],
        filesId: [],
        filmCuts: [],
        filmWidth: 0,
        filmHeight: 0,
        filmSize: 0,
        carBrand: "",
        carModel: "",
        isModelUnknown: false,
        carImageUrl: "",
        hasProblematicPattern: false,
        hasProblematicPackaging: false,
        wasCut: false,
        hasNoPatterns: false,
        patternRequestSent: false,
        noPatternsType: '',
        assemblyPrograms: [],
      };
      this.fileRecords = [];
      this.imageRecords = [];
      this.clientData = {};
      this.fileRecordsForUpload = [];
    },
    mapChanges(type, changes) {
      const oldValue = this.oldOrderData;

      switch (type) {
        case 'image':
          if ((oldValue.imagesId?.length || 0) !== this.imageRecords.length) {
            this.updateData.image = {
              before: oldValue.imagesId?.length || 0,
              after: this.imageRecords.length
            };
          }
          break;

        case 'image_add':
          if (!this.updateData.imageEvents) this.updateData.imageEvents = [];
          this.updateData.imageEvents.push({
            action: 'added',
            index: this.imageRecords.length,
            name: changes.name || 'unnamed'
          });
          break;

        case 'image_delete':
          if (!this.updateData.imageEvents) this.updateData.imageEvents = [];
          this.updateData.oldImageNames = [...this.imageRecords.map(img => img.name)];
          this.updateData.imageEvents.push({
            action: 'deleted',
            index: changes.index + 1,
            name: changes.name || 'unnamed'
          });
          break;

        case 'file':
          if ((oldValue.filesId?.length || 0) !== this.fileRecords.length) {
            this.updateData.file = {
              before: oldValue.filesId?.length || 0,
              after: this.fileRecords.length
            };
          }
          break;

        case 'file_add':
          if (!this.updateData.fileEvents) this.updateData.fileEvents = [];
          this.updateData.fileEvents.push({
            action: 'added',
            index: this.fileRecords.length,
            name: changes.name || 'unnamed'
          });
          break;

        case 'file_delete':
          if (!this.updateData.fileEvents) this.updateData.fileEvents = [];
          if (!this.updateData.oldFileNames) {
            this.updateData.oldFileNames = this.fileRecords.map(f => f.name);
          }
          this.updateData.fileEvents.push({
            action: 'deleted',
            index: changes.index + 1,
            name: changes.name || 'unnamed'
          });
          break;

        case 'status':
          if (oldValue.status !== this.orderData.status) {
            this.updateData.status = {
              before: oldValue.status || '—',
              after: this.orderData.status || '—'
            };
          }
          break;

        case 'title':
          if (oldValue.title !== changes) {
            this.updateData.title = {
              before: oldValue.title || '—',
              after: changes || '—'
            };
          }
          break;

        case 'description':
          if (oldValue.description !== changes) {
            this.updateData.description = {
              before: oldValue.description || '—',
              after: changes || '—'
            };
          }
          break;

        case 'carBrand':
          if (oldValue.carBrand !== changes) {
            this.updateData.carBrand = {
              before: oldValue.carBrand || '—',
              after: changes || '—'
            };
          }
          break;

        case 'carModel':
          if (oldValue.carModel !== changes) {
            this.updateData.carModel = {
              before: oldValue.carModel || '—',
              after: changes || '—'
            };
          }
          break;

        case 'carYear':
          if (oldValue.carYear !== changes) {
            this.updateData.carYear = {
              before: oldValue.carYear || '—',
              after: changes || '—'
            };
          }
          break;

        case 'isUrgent':
          if (oldValue.isUrgent !== changes) {
            this.updateData.isUrgent = {
              before: oldValue.isUrgent, // Відправляємо чистий boolean
              after: changes
            };
          }
          break;

        case 'hasProblematicPattern':
          if (oldValue.hasProblematicPattern !== changes) {
            this.updateData.hasProblematicPattern = {
              before: oldValue.hasProblematicPattern,
              after: changes
            };
          }
          break;

        case 'hasProblematicPackaging':
          if (oldValue.hasProblematicPackaging !== changes) {
            this.updateData.hasProblematicPackaging = {
              before: oldValue.hasProblematicPackaging,
              after: changes
            };
          }
          break;

        case 'hasNoPatterns':
          if (oldValue.hasNoPatterns !== changes) {
            this.updateData.hasNoPatterns = {
              before: oldValue.hasNoPatterns,
              after: changes
            };
          }
          break;

        case 'patternRequestSent':
          if (oldValue.patternRequestSent !== changes) {
            this.updateData.patternRequestSent = {
              before: oldValue.patternRequestSent,
              after: changes
            };
          }
          break;

        case 'noPatternsType': {
          const noPatternsLabel = (val) => {
            if (val === 'none_in_any') return this.t('labels.noPatternsNoneInAny', 'Немає лекал в жодній з баз');
            if (val === 'none_in_ours') return this.t('labels.noPatternsNoneInOurs', 'Немає лекал в наших базах');
            if (val === 'partial') return this.t('labels.noPatternsPartial', 'Є лекала частково');
            return '—';
          };
          if (oldValue.noPatternsType !== changes) {
            this.updateData.noPatternsType = {
              before: noPatternsLabel(oldValue.noPatternsType),
              after: noPatternsLabel(changes)
            };
          }
          break;
        }

        case 'assemblyPrograms': {
          const oldPrograms = Array.isArray(oldValue.assemblyPrograms) ? [...oldValue.assemblyPrograms].sort() : [];
          const newPrograms = Array.isArray(changes) ? [...changes].sort() : [];
          if (oldPrograms.join(',') !== newPrograms.join(',')) {
            this.updateData.assemblyPrograms = {
              before: helpers.getAssemblyProgramLabels(oldPrograms) || '—',
              after: helpers.getAssemblyProgramLabels(newPrograms) || '—'
            };
          }
          break;
        }

        case 'clientId':
          if (this.oldOrderData.clientId !== this.clientData?._id) {
            this.updateData.clientId = {
              before: this.oldClientData?.name || '—',
              after: this.clientData?.name || '—'
            };
          }
          break;

        case 'managerId':
          if (this.oldOrderData.managerId !== this.managerData?._id) {
            this.updateData.managerId = {
              before: this.oldManagerData?.username || '—',
              after: this.managerData?.username || '—'
            };
          }
          break;

        case 'badgeId': {
          const oldBadgeId = typeof oldValue.badgeId === 'object'
              ? oldValue.badgeId?._id
              : oldValue.badgeId;

          if (oldBadgeId !== changes) {
            const oldBadge = this.badges[oldBadgeId];
            const newBadge = this.badges[changes];

            this.updateData.badgeId = {
              before: oldBadge || '—',
              after: newBadge || '—'
            };
          }
          break;
        }

        case 'cuts':
          if (this.$store.getters.isDraftsman) {
            const formatCutDetails = (cut, index) => {
              let details = `#${index + 1} (${cut.width || 0}x${cut.height || 0}м)`;
              if (cut.note) {
                details += ` - ${this.t('log.notePrefix', 'Примітка')}: ${cut.note}`;
              }
              return details;
            };

            const beforeCuts = (oldValue.filmCuts || [])
                .map(formatCutDetails)
                .join('\n') || this.t('log.noItems', 'Не було');

            const afterCuts = (changes || [])
                .map(formatCutDetails)
                .join('\n');

            if (beforeCuts !== afterCuts) {
              this.updateData.cuts = {
                before: beforeCuts,
                after: afterCuts || this.t('log.allCutsDeleted', 'Видалено всі викрійки')
              };
            }
          }
          break;

        default:
          break;
      }
    },
    formatSafeUrl(path) {
      if (!path) return '';
      return '/' + path.replace(/^\/+/, '').replace(/\\/g, '/');
    },
    formatFileSize(size) {
      if (!size || isNaN(size) || size === 0) return 'n/a';
      const i = Math.floor(Math.log(size) / Math.log(1024));
      return (size / Math.pow(1024, i)).toFixed(2) + ' ' + ['B', 'KB', 'MB', 'GB', 'TB'][i];
    },
    formatFullDate(date) {
      return helpers.formatFullDate(date);
    },
    handleAudioWasRead(data) {
      log.info('View:OrderForm:on:audio_was_read:RECEIVED', { data: data });

      const audioFile = this.fileRecords.find(f => f.id === data.fileId);
      if (!audioFile) {
        log.warn('View:OrderForm:on:audio_was_read:FILE_NOT_FOUND', { fileId: data.fileId });
        return;
      }

      if (!audioFile.readBy) {
        audioFile.readBy = [];
      }

      const alreadyExists = audioFile.readBy.find(u => u._id === data.userWhoRead._id);

      if (!alreadyExists) {
        log.info('View:OrderForm:on:audio_was_read:UPDATING_UI', { fileId: data.fileId });
        audioFile.readBy.push(data.userWhoRead);
      }
    },
    async getBadges() {
      try {
        const res = await $http.get('/api/badges');
        this.badges = res.data.reduce((acc, badge) => {
          acc[badge._id] = helpers.getBadgeName(badge, this.$i18n.locale);
          return acc;
        }, {});
      } catch (err) {
        log.error('View:OrderForm:getBadges', {error: err});
      }
    },
    escapeHtml(str) {
      return String(str ?? '').replace(/[&<>"']/g, s => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[s]));
    },
    buildClientNotesHtml(notes) {
      return notes.map(note => {
        const meta = [note.author, note.createdAt ? helpers.formatFullDate(note.createdAt) : '']
            .filter(Boolean).join(' • ');
        return '<div style="text-align:left;margin-bottom:8px;padding:8px 10px;background:#fff8e6;border-left:3px solid #ffc107;border-radius:6px;">'
            + `<div>${this.escapeHtml(note.text)}</div>`
            + (meta ? `<div style="font-size:11px;color:#adb5bd;margin-top:3px;">${this.escapeHtml(meta)}</div>` : '')
            + '</div>';
      }).join('');
    },
    maybeShowClientNotesModal() {
      const role = this.currentUserRole;
      if (role !== 'draftsman' && role !== 'admin') return;

      const notes = this.clientData?.notes || [];
      if (!notes.length) return;

      const userId = this.$store.getters.userData?._id;
      const ackList = (this.orderData.clientNotesAcknowledgedBy || []).map(id => String(id?._id || id));
      if (ackList.includes(String(userId))) return;

      this.$swal.fire({
        title: this.t('labels.clientNotes', 'Примітки клієнта'),
        html: this.buildClientNotesHtml(notes),
        icon: 'info',
        confirmButtonText: this.t('buttons.acknowledged', 'Ознайомився'),
        allowOutsideClick: false,
        allowEscapeKey: false
      }).then(result => {
        if (result.isConfirmed) {
          this.$http.put(`/api/orders/${this.orderId}/acknowledge-client-notes`).then(() => {
            if (!Array.isArray(this.orderData.clientNotesAcknowledgedBy)) {
              this.orderData.clientNotesAcknowledgedBy = [];
            }
            this.orderData.clientNotesAcknowledgedBy.push(userId);
          }).catch(err => {
            log.error('View:OrderForm:acknowledgeClientNotes', {error: err});
          });
        }
      });
    },
    cloneOrder() {
      Swal.fire({
        title: this.t('buttons.clone'),
        text: this.t('asks.confirmClone'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.t('buttons.confirm'),
        cancelButtonText: this.t('buttons.cancel'),
        showLoaderOnConfirm: true,
        preConfirm: () => {
          return this.$http.post(`/api/orders/clone/${this.orderId}`)
              .then(response => response.data)
              .catch(error => {
                Swal.showValidationMessage(`Request failed: ${error}`);
              });
        },
        allowOutsideClick: () => !Swal.isLoading()
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire(this.t('alerts.success'), this.t('messages.orderCloned'), 'success');
          this.router.push(`/order/${result.value._id}`);
        }
      });
    }
  },
  mounted() {
    document.addEventListener('paste', this.handlePaste);

    this.$.exposed = {
      resetForm: this.resetForm,
      submissionComplete: this.submissionComplete
    };

    if (this.$socket && this.orderId) {
      this.$socket.emit('joinRoom', { roomId: this.orderId }, (response) => {
        if (response?.status === 'ok') {
          log.info('View:OrderForm:Socket', { message: 'Successfully joined the room', orderId: this.orderId });
        } else {
          log.error('View:OrderForm:Socket', { message: 'Unable to join the room', error: response?.error });
        }
      });

      this.$socket.on('audio:was_read', this.handleAudioWasRead);
    }

    // Сначала загружаем badges
    this.getBadges();

    // Проверяем, редактируем ли мы существующий заказ
    if (this.orderId) {
      const savedState = localStorage.getItem(`audioCollapseState_${this.orderId}`);

      if (savedState !== null) {
        this.isAudioCollapseOpen = JSON.parse(savedState);
      }

      this.$http.get("/api/orders/" + this.orderId).then(async (res) => {
        // 1. Получаем основные данные заказа
        this.orderData = res.data;

        this.$emit('orderLoaded', res.data);

        await this.initCarData();

        // 2. Конвертируем badgeId в строку, если это объект
        if (typeof this.orderData.badgeId === 'object' && this.orderData.badgeId?._id) {
          this.orderData.badgeId = this.orderData.badgeId._id;
        }

        const loadedPrograms = Array.isArray(this.orderData.assemblyPrograms) ? this.orderData.assemblyPrograms : [];
        const cutPrograms = (this.orderData.filmCuts || []).map(cut => cut.program).filter(Boolean);
        this.orderData.assemblyPrograms = [...new Set([...loadedPrograms, ...cutPrograms])];

        // 3. Делаем "снимок" для дальнейшего сравнения
        this.oldOrderData = JSON.parse(JSON.stringify(this.orderData));
        this._cutsBaseline = JSON.parse(JSON.stringify(this.orderData.filmCuts || []));

        // 4. Загружаем связанные данные
        if (this.orderData.clientId) {
          this.$http.get('/api/clients/' + this.orderData.clientId).then(response => {
            this.clientData = response.data;
            this.oldClientData = JSON.parse(JSON.stringify(response.data));
            this.maybeShowClientNotesModal();
          });
        }
        if (this.orderData.managerId) {
          this.$http.get('/api/user/' + this.orderData.managerId).then(response => {
            this.managerData = response.data;
            this.oldManagerData = JSON.parse(JSON.stringify(response.data));
          });
        }
        if (this.orderData.imagesId.length > 0) {
          this.$http.get('/api/files/', {params: {ids: this.orderData.imagesId}}).then(response => {
            this.imageRecords = response.data.map(file => ({
              id: file._id,
              name: file.fileName,
              type: file.type,
              size: file.size || 0,
              sizeText: this.formatFileSize(file.size || 0),
              file: {
                name: file.fileName,
                type: file.type,
                size: file.size || 0
              },
              url: '/api/download/' + file._id,
              meta: {
                name: file.fileName,
                size: file.size || 0,
                type: file.type
              }
            }));
            this.oldImageRecords = JSON.parse(JSON.stringify(this.imageRecords));
          });
        }
        if (this.orderData.filesId.length > 0) {
          this.$http.get('/api/files/', {params: {ids: this.orderData.filesId}}).then(async response => {

            const parsedFiles = [];

            for (const file of response.data) {
              let fileUrl = '/api/download/' + file._id;
              let safeSize = file.size || 0;

              const ext = (file.fileName || "").toUpperCase();

              let isPatternFileFlag = false;

              if ((ext.endsWith('.CWE') || ext.endsWith('.MMM') || ext.endsWith('.BAK')) && safeSize < 500) {
                isPatternFileFlag = true;
                try {
                  const contentRes = await this.$http.get(fileUrl, { responseType: 'text' });
                  const textContent = contentRes.data;

                  if (typeof textContent === 'string' && textContent.includes('External File Link:')) {
                    fileUrl = textContent.replace('External File Link:', '').trim();
                  }
                } catch (err) {
                  console.warn('Не вдалося прочитати лінку лекала:', err);
                }
              }

              parsedFiles.push({
                id: file._id,
                name: file.fileName,
                type: file.type,
                size: safeSize,
                sizeText: this.formatFileSize(safeSize),
                createdAt: file.createdAt,
                uploader: file.uploader,
                readBy: file.readBy || [],
                url: fileUrl,
                file: {
                  name: file.fileName,
                  type: file.type,
                  size: safeSize
                },
                meta: {
                  name: file.fileName,
                  size: safeSize,
                  type: file.type
                },
                isPatternFile: isPatternFileFlag
              });
            }

            this.fileRecords = parsedFiles;
            this.oldFileRecords = JSON.parse(JSON.stringify(this.fileRecords));
          });
        }
      });
    } else {
      this.oldOrderData = JSON.parse(JSON.stringify(this.orderData));

      this.initCarData();
    }
  },
  computed: {
    pendingPatternsCount() {
      return this.fileRecords.filter(f => f.isPatternFile && String(f.id).startsWith('pattern_')).length;
    },
    isOrderCreator() {
      const currentUserId = this.$store.getters.userData._id;
      if (this.orderId) {
        return this.orderData.managerId === currentUserId;
      } else {
        return true;
      }
    },
    patternsExternalLink() {
      if (!this.orderData.carBrand || !this.orderData.carModel) {
        return '#';
      }

      const brandIndex = this.brandsList.indexOf(this.orderData.carBrand);

      const brandId = brandIndex !== -1 ? brandIndex + 1 : this.orderData.carBrand;

      const model = encodeURIComponent(this.orderData.carModel);

      return `https://patterns.brawix.com/car-films-cut-patterns?filters[main_filter][car_manufacturer_id]=${brandId}&filters[main_filter][car_model]=${model}`;
    },
    audioRecords() {
      return this.fileRecords.filter(f => f.type && f.type.startsWith('audio/'));
    },
    nonAudioRecords() {
      return this.fileRecords.filter(f => !f.type || !f.type.startsWith('audio/'));
    },
    orderManager() {
      if (!this.managerData._id && !this.orderId) return this.$store.getters.userData._id;
      return this.managerData._id;
    },
    currentUserRole() {
      return this.$store.getters.userData?.role?.name;
    },
    isDistributor() {
      return this.currentUserRole === 'distributor';
    },
    isOrderFromDistributor() {
      return this.managerData?.role?.name === 'distributor';
    },
    badgesOptions() {
      return Object.entries(this.badges).map(([value, label]) => ({
        value,
        label
      }));
    }
  },
}
</script>

<template>
  <div class="order">
    <form action="">
      <div class="row g-3">
        <OrderGeneralInfo
            :order-data="orderData"
            :order-id="orderId"
            :compact="compact"
            :badges-options="badgesOptions"
            :temp-orders="tempOrders"
            :is-distributor="isDistributor"
            :current-user-role="currentUserRole"
            :old-order-status="oldOrderData.status"
            :is-submitting="isSubmitting"
            :brands-list="brandsList"
            :models-list="modelsList"
            :years-list="yearsList"
            @open-patterns-modal="openPatternsModal"
            @fetch-models="fetchModels"
            @fetch-years="fetchYears"
            @map-changes="mapChanges"
            @find-orders="asyncFindOrders"
            @select-order="handleSelectOrder"
            @send-to-cutting="sendToCutting"
            @cutting-done="setCuttingDone"
            @clone-order="cloneOrder"
            @update-data="handleUpdateData"
        />
        <OrderActors
            v-model:client-data="clientData"
            v-model:manager-data="managerData"
            v-model:add-client="addClient"
            :temp-clients="tempClients"
            :temp-users="tempUsers"
            :order-id="orderId"
            :is-distributor="isDistributor"
            :is-order-from-distributor="isOrderFromDistributor"
            :current-user-role="currentUserRole"
            @search-clients="asyncFind"
            @search-managers="asyncFindUsers"
            @map-changes="mapChanges"
        />

        <OrderAttachments
            v-if="!hideAttachmentsUpload"
            :title="t('labels.carPhotos', 'Фото автомобіля')"
            :records="imageRecords"
            :upload-url="uploadUrl"
            :upload-headers="uploadHeaders"
            :is-gallery="true"
            zone-name="carPhotos"
            :hide-dropzone="hideAttachmentsUpload"
            @upload="imageFilesUpload"
            @delete-request="onBeforeDelete($event, 'image')"
            @set-active-zone="setActiveZone"
        />

        <OrderAudioNotes
            :order-id="orderId"
            :audio-records="audioRecords"
            :is-order-creator="isOrderCreator"
            @uploaded="handleAudioUploaded"
            @delete="onBeforeDelete($event, 'file')"
            @read="markAudioAsRead"
            @update:isSubmitting="isSubmitting = $event"
        />

        <OrderAttachments
            :title="t('labels.files', 'Файли')"
            :records="nonAudioRecords"
            :upload-url="uploadUrl"
            :upload-headers="uploadHeaders"
            zone-name="orderFiles"
            :hide-dropzone="hideAttachmentsUpload"
            @upload="filesRecordsUpload"
            @delete-request="onBeforeDelete($event, 'file')"
            @set-active-zone="setActiveZone"
        />

        <OrderAttachments
            v-if="hideAttachmentsUpload"
            :title="t('labels.carPhotos', 'Фото автомобіля')"
            :records="imageRecords"
            :upload-url="uploadUrl"
            :upload-headers="uploadHeaders"
            :is-gallery="true"
            zone-name="carPhotos"
            @upload="imageFilesUpload"
            @delete-request="onBeforeDelete($event, 'image')"
            @set-active-zone="setActiveZone"
        />

        <div class="col-12" v-if="orderId">
          <div v-if="cutsSaveState" class="cuts-save-state small mb-2" :class="{
                 'text-secondary': cutsSaveState === 'saving',
                 'text-success': cutsSaveState === 'saved',
                 'text-danger': cutsSaveState === 'error'
               }">
            <template v-if="cutsSaveState === 'saving'">
              <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {{ t('messages.cutsSaving', 'Збереження лекал…') }}
            </template>
            <template v-else-if="cutsSaveState === 'saved'">
              <i class="bi bi-check-circle-fill me-1"></i>{{ t('messages.cutsSaved', 'Лекала збережено') }}
            </template>
            <template v-else-if="cutsSaveState === 'error'">
              <i class="bi bi-exclamation-triangle-fill me-1"></i>{{ t('messages.cutsSaveError', 'Не вдалося зберегти лекала') }}
            </template>
          </div>
          <FilmCutsEditor v-if="orderId" :can-edit-program="$store.getters.isDraftsman" @cutsChange="onCutsChange" @programChange="onCutProgramChange" @cutRemoved="onCutRemoved" v-model="orderData.filmCuts"/>
        </div>

        <div class="col-12">
          <button type="button" :class="{ 'loading': isSubmitting }" class="btn btn-primary my-4 w-100" @click="submitOrder" :disabled="isSubmitting">
            <template v-if="isSubmitting && pendingPatternsCount > 0">
              <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ t('messages.downloadingPatterns', 'Завантажуємо лекала на сервер...') }}
            </template>
            <template v-else>
              <template v-if="orderId">
                {{ t('buttons.update') }}
              </template>
              <template v-else>
                {{ t('buttons.add') }}
              </template>
            </template>
          </button>
        </div>
      </div>
    </form>
  </div>

  <div v-if="patternsModal" class="modal-backdrop fade show"></div>
  <div v-if="patternsModal" class="modal d-block" tabindex="-1" style="background: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-xl modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header bg-light">
          <h5 class="modal-title">
            <i class="bi bi-car-front me-2"></i>
            {{ t('patterns.databaseTitle') }}: {{ orderData.carBrand }} {{ orderData.carModel }} ({{ orderData.carYear }})
          </h5>
          <button type="button" class="btn-close" @click="patternsModal = false"></button>
        </div>

        <div class="modal-body">
          <div v-if="loadingPatterns" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-2">{{ t('patterns.searching') }}</p>
          </div>

          <div v-else-if="!selectedPatternItem" class="row g-3">

            <div class="col-12 mb-3" v-if="orderData.carBrand && orderData.carModel">
              <a :href="patternsExternalLink" target="_blank" class="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center" style="border-style: dashed;">
                <i class="bi bi-box-arrow-up-right me-2"></i>
                {{ t('patterns.openOnWebsite', { brand: orderData.carBrand, model: orderData.carModel }) }}
              </a>
            </div>

            <div v-if="patternsItems.length === 0" class="text-center py-5">
              <p class="text-muted">{{ t('patterns.notFound') }}</p>
            </div>

            <div v-for="pattern in patternsItems" :key="pattern.id_pattern" class="col-md-4 col-lg-3">
              <div class="card h-100 shadow-sm pattern-card" @click="handleCardClick($event, pattern.id_pattern)" style="cursor:pointer; transition: 0.3s">
                <div class="position-relative">
                  <a v-if="pattern.photo" class="photo-preview-container" :href="pattern.photo.startsWith('http') ? pattern.photo : 'https://patterns.brawix.com' + pattern.photo" data-fancybox="gallery-cars" >
                    <img :src="pattern.photo.startsWith('http') ? pattern.photo : 'https://patterns.brawix.com' + pattern.photo" class="card-img-top p-2 w-100 h-100" alt="Car photo" style="object-fit: contain; min-height: 150px;">
                    <div class="hover-overlay d-flex align-items-center justify-content-center position-absolute top-0 start-0 w-100 h-100 rounded">
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        <line x1="11" y1="8" x2="11" y2="14"></line>
                        <line x1="8" y1="11" x2="14" y2="11"></line>
                      </svg>
                    </div>
                  </a>
                    <div v-else class="bg-light d-flex align-items-center justify-content-center" style="height: 150px">
                    <i class="bi bi-image text-secondary fs-1"></i>
                  </div>
                </div>
                <div class="card-body border-top">
                  <h6 class="card-title mb-1 text-primary">{{ pattern.car_name }}</h6>
                  <p class="card-text small text-muted mb-0">
                    <i class="bi bi-calendar3 me-1"></i> {{ pattern.car_years }}
                  </p>
                  <p class="card-text small text-muted" v-if="pattern.body_type">
                    <i class="bi bi-truck me-1"></i> {{ pattern.body_type }}
                  </p>
                </div>

                <div class="card-footer bg-transparent border-0 pb-3 d-flex flex-column gap-2">
                  <button class="btn btn-sm btn-outline-primary w-100 text-uppercase fw-bold" style="font-size: 10px">
                    {{ t('patterns.viewPatterns') }}
                  </button>

                  <a :href="`${patternsExternalLink}&filters[main_filter][pattern_id]=${pattern.id_pattern}&filters[main_filter][id]=${pattern.id_pattern}`"
                     target="_blank"
                     class="btn btn-sm btn-primary w-100 text-uppercase fw-bold"
                     style="font-size: 10px"
                     @click.stop>
                    <i class="bi bi-box-arrow-up-right me-1"></i> {{ t('patterns.openOnSite') }}
                  </a>
                </div>

              </div>
            </div>
          </div>

          <div v-else class="bg-light-theme p-4 rounded mt-3">
            <button class="back_to_cars-btn btn btn-sm btn-outline-secondary mb-4 d-flex align-items-center" @click="fetchPatternsItems()">
              <svg class="back_to_cars-icon" xmlns="http://www.w3.org/2000/svg" width="25" height="20" fill="none" viewBox="0 0 24 24"><path fill="#6c757d" fill-rule="evenodd" d="M11.707 4.293a1 1 0 0 1 0 1.414L6.414 11H20a1 1 0 1 1 0 2H6.414l5.293 5.293a1 1 0 0 1-1.414 1.414l-7-7a1 1 0 0 1 0-1.414l7-7a1 1 0 0 1 1.414 0" clip-rule="evenodd"/></svg>
              {{ t('patterns.backToCars') }}
            </button>

            <div class="notes_gallery_collapse mb-3"
                 v-if="orderData.main_note || (orderData.notes_gallery && orderData.notes_gallery.length > 0) || patternsParts.packs?.some(pack => pack.cuts?.some(cut => cut.note))"
                 :class="{ 'collapsed': !isDemoCollapsed, '': isDemoCollapsed }">
              <div class="notes_header bg-danger p-2 d-flex align-items-center w-100 justify-content-between" @click="isDemoCollapsed = !isDemoCollapsed">
                <div class="heading fs-5 text-white fw-bold d-flex align-items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" fill="none" viewBox="0 0 24 24"><g fill="#fff"><path fill-rule="evenodd" d="M12.898 3.614a1 1 0 0 0-1.796 0L3.306 19.561A1 1 0 0 0 4.204 21h15.592a1 1 0 0 0 .898-1.44zm-3.593-.878c1.097-2.243 4.294-2.243 5.39 0l7.796 15.946c.974 1.993-.476 4.318-2.695 4.318H4.204c-2.218 0-3.67-2.325-2.695-4.318z" clip-rule="evenodd"/><path d="M11 8.5a1 1 0 1 1 2 0V14a1 1 0 1 1-2 0zM13.5 18a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0"/></g></svg>
                  <span class="heading d-block">{{ t('titles.attentionNotes') }}</span>
                </div>
                <svg class="chevron-down d-block" xmlns="http://www.w3.org/2000/svg" width="25" height="25" fill="none" viewBox="0 0 24 24"><path fill="#fff" fill-rule="evenodd" d="m12 13.94 5.47-5.47 1.06 1.06L12 16.06 5.47 9.53l1.06-1.06z" clip-rule="evenodd"/></svg>              </div>
              <div class="notes-body" v-show="!isDemoCollapsed">
                <div class="notes_body-wrapper border border-danger bg-white row mx-0 py-3">
                  <div v-if="orderData.main_note" class="main_note-container align-items-start col-lg-4 col-md-6 mb-2 col-12">
                    <div class="main_note position-relative border border-gray rounded d-flex">
                      <div class="title_note-block position-absolute text-dark w-100 bg-warning bg-opacity-75 px-2">
                        <strong class="d-block text-center">{{ t('titles.mainNote') }}</strong>
                      </div>
                      <div class="note-block w-100 pt-4 pb-1 d-flex flex-column justify-content-center">
                        <span class="d-block text-center px-1 text-break">{{ orderData.main_note }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-if="orderData.notes_gallery && orderData.notes_gallery.length > 0" class="additional-notes mb-2 col-lg-4 col-md-6 col-12">
                    <div class="additional_notes-item rounded border position-relative border-gray">
                      <template v-for="(note, index) in orderData.notes_gallery" :key="index">
                        <div class="title_block-img text-dark position-absolute w-100 bg-warning bg-opacity-75 mb-2 px-2 text-center">
                          <strong class="text-center d-block">Note/{{note.id}}</strong>
                        </div>
                        <div class="note_block-wrapper d-flex flex-row-reverse align-items-center">
                          <span class="text-center col-8 d-block" v-if="note.text">{{note.text}}</span>
                          <div class="note_img-block d-flex justify-content-center col-4">
                            <a v-if="note.image" :href="note.image" data-fancybox="gallery-notes" :data-caption="note.text">
                              <img :src="note.image" :alt="note.text" class="additional_note-image h-auto rounded">
                            </a>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                  <template v-if="patternsParts.packs?.some(pack => pack.cuts?.some(cut => cut.note))">
                    <template v-for="(packNote, packIndex) in patternsParts.packs" :key="'pack-' + packIndex">
                      <template v-for="(cutNote, cutIndex) in packNote.cuts" :key="'cut-' + cutIndex">
                        <div v-if="cutNote.note" class="packs_gallery-container mb-2 col-lg-4 col-md-6 col-12">
                          <div class="packs_gallery-notes position-relative border d-flex border-gray rounded">
                            <div class="title_note-block position-absolute text-dark px-2 w-100 bg-warning bg-opacity-75">
                              <strong class="text-center d-block">{{ packNote.package_name }}</strong>
                            </div>
                            <div class="note-block w-100 pt-4 pb-1 d-flex flex-column justify-content-center text-break">
                              <span class="text-center px-1 d-block">{{ cutNote.note }}</span>
                            </div>
                          </div>
                        </div>
                      </template>
                    </template>
                  </template>
                </div>
              </div>
            </div>
            <div v-if="patternsParts && patternsParts.packs" class="row g-3 align-items-start">

              <div v-for="pack in patternsParts.packs" :key="pack.package_id" class="col-12 col-md-6 col-xl-4">

                <div class="package-card">
                  <div class="package-header" @click="pack.collapsed = !pack.collapsed">
                    <span>{{ pack.package_name }} ({{ pack.cuts_count }})</span>
                    <i class="bi text-muted" :class="pack.collapsed ? 'bi-chevron-down' : 'bi-chevron-up'"></i>
                  </div>

                  <div class="package-body" v-show="!pack.collapsed">
                    <div v-for="cut in pack.cuts" :key="cut.id"
                         class="cut-card position-relative mb-3"
                         :style="isCutSelected(cut.id) ? 'border-color: #0d6efd; background-color: #f0f7ff; cursor: pointer;' : 'cursor: pointer;'"
                         @click="addPartToOrder(cut)">

                      <div class="position-absolute top-0 end-0 m-2" style="z-index: 5;">
                        <i class="bi fs-4" :class="isCutSelected(cut.id) ? 'bi-check-circle-fill text-primary' : 'bi-circle text-muted opacity-25'"></i>
                      </div>

                      <div class="cut-header pe-4">
                        <div>{{ t('patterns.cutId', 'ID викрійки:') }} <strong>{{ cut.id }}</strong></div>
                        <div v-if="cut.note" class="mt-2 p-1 px-2 border border-warning rounded d-flex align-items-start" style="font-size: 10px; line-height: 1.3; color: #856404; background-color: #fff3cd;">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#ffc107" class="flex-shrink-0 me-2" viewBox="0 0 16 16" style="margin-top: 1px;">
                            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                          </svg>
                          <span>{{ cut.note }}</span>
                        </div>
                      </div>

                      <div class="cut-preview-container">
                        <div class="dimension-v">
                          <span class="dots">---------</span>
                          <span class="value">{{ cut.width || '0.00' }} {{ t('patterns.meter', 'Метр') }}</span>
                          <span class="dots">---------</span>
                        </div>

                        <div class="cut-image">
                          <img :src="cut.image"
                               alt="vector"
                               @error="(e) => e.target.style.display = 'none'"
                               :style="isCutSelected(cut.id) ? 'opacity: 0.6; mix-blend-mode: multiply;' : 'mix-blend-mode: multiply;'">
                        </div>

                        <div class="dimension-h">
                          <span class="dots">---------</span>
                          <span class="value">{{ cut.height || '0.00' }} {{ t('patterns.meter') }}</span>
                          <span class="dots">---------</span>
                        </div>
                        <div v-if="isCutSelected(cut.id)"
                             class="text-success mt-2 p-1 px-2 border border-success rounded d-flex align-items-center justify-content-center w-100"
                             style="font-size: 11px; font-weight: 600; background-color: #F0FFF0;">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="flex-shrink-0 me-1" viewBox="0 0 16 16">
                            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                          </svg>
                          <span>{{ t('patterns.addedToCuts') }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

.bg-light-theme {
  background-color: #f8f9fa;
  color: #212529;
}

.package-card {
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);

  .package-header {
    padding: 14px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    font-weight: 600;
    font-size: 14px;
    color: #212529;
    user-select: none;
    transition: background 0.2s;

    &:hover {
      background: #f1f3f5;
    }
  }

  .package-body {
    padding: 16px;
    border-top: 1px solid #dee2e6;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: #fafafa;
  }
}

.cut-card {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 14px;
  color: #6c757d;
  font-size: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: #b6d4fe !important;
    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.1);
    transform: translateY(-2px);
  }

  .cut-header {
    display: flex;
    flex-direction: column;
    margin-bottom: 12px;
    strong { color: #212529; }
  }

  .cut-preview-container {
    position: relative;
    padding: 15px 15px 35px 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .cut-image {
      background: #f8f9fa;
      border: 1px dashed #ced4da;
      border-radius: 4px;
      padding: 10px;
      width: 100%;
      min-height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      overflow: hidden;
      position: relative;

      img {
        width: 100%;
        max-height: 160px;
        object-fit: contain;
        transition: opacity 0.2s;
        mix-blend-mode: multiply;
      }

      .add-overlay {
        position: absolute;
        inset: 0;
        background: rgba(255, 255, 255, 0.85);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.2s;
        border-radius: 4px;

        i {
          font-size: 2.5rem;
          color: #0d6efd;
        }
      }

      &:hover {
        img { opacity: 0.2; }
        .add-overlay { opacity: 1; }
      }
    }

    .dimension-v {
      position: absolute;
      left: -22%;
      top: 50%;
      transform: translateY(-50%) rotate(-90deg);
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 10px;
      white-space: nowrap;
      color: #adb5bd;

      .value {
        border: 1px solid #ced4da;
        padding: 2px 6px;
        border-radius: 4px;
        background: #ffffff;
        color: #495057;
        font-weight: 500;
      }
    }

    .dimension-h {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      font-size: 10px;
      color: #adb5bd;

      .dots { opacity: 0.7; letter-spacing: 2px;}
      .value {
        border: 1px solid #ced4da;
        padding: 2px 6px;
        border-radius: 4px;
        background: #ffffff;
        color: #495057;
        font-weight: 500;
      }
    }
  }
}

.pattern-card {
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
    border-color: #0d6efd;
  }
}

.search-orders {
  max-height: 200px;
  overflow-y: scroll;
}

.loading {
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: #fff;
    opacity: 0.5;
    z-index: 100;
  }

  &::after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-position: center;
    background-size: 100px;
    background-repeat: no-repeat;
    background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwcHgiICBoZWlnaHQ9IjIwMHB4IiAgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCIgcHJlc2VydmVBc3BlY3RSYXRpbz0ieE1pZFlNaWQiIGNsYXNzPSJsZHMtcmlwcGxlIiBzdHlsZT0iYmFja2dyb3VuZDogbm9uZTsiPjxjaXJjbGUgY3g9IjUwIiBjeT0iNTAiIHI9IjYuMTM4NDciIGZpbGw9Im5vbmUiIG5nLWF0dHItc3Ryb2tlPSJ7e2NvbmZpZy5jMX19IiBuZy1hdHRyLXN0cm9rZS13aWR0aD0ie3tjb25maWcud2lkdGh9fSIgc3Ryb2tlPSIjMzM3YWI3IiBzdHJva2Utd2lkdGg9IjIiPjxhbmltYXRlIGF0dHJpYnV0ZU5hbWU9InIiIGNhbGNNb2RlPSJzcGxpbmUiIHZhbHVlcz0iMDs0MCIga2V5VGltZXM9IjA7MSIgZHVyPSIxIiBrZXlTcGxpbmVzPSIwIDAuMiAwLjggMSIgYmVnaW49Ii0wLjVzIiByZXBlYXRDb3VudD0iaW5kZWZpbml0ZSI+PC9hbmltYXRlPjxhbmltYXRlIGF0dHJpYnV0ZU5hbWU9Im9wYWNpdHkiIGNhbGNNb2RlPSJzcGxpbmUiIHZhbHVlcz0iMTswIiBrZXlUaW1lcz0iMDsxIiBkdXI9IjEiIGtleVNwbGluZXM9IjAuMiAwIDAuOCAxIiBiZWdpbj0iLTAuNXMiIHJlcGVhdENvdW50PSJpbmRlZmluaXRlIj48L2FuaW1hdGU+PC9jaXJjbGU+PGNpcmNsZSBjeD0iNTAiIGN5PSI1MCIgcj0iMjguMzk3NSIgZmlsbD0ibm9uZSIgbmctYXR0ci1zdHJva2U9Int7Y29uZmlnLmMyfX0iIG5nLWF0dHItc3Ryb2tlLXdpZHRoPSJ7e2NvbmZpZy53aWR0aH19IiBzdHJva2U9IiM1YmMwZGUiIHN0cm9rZS13aWR0aD0iMiI+PGFuaW1hdGUgYXR0cmlidXRlTmFtZT0iciIgY2FsY01vZGU9InNwbGluZSIgdmFsdWVzPSIwOzQwIiBrZXlUaW1lcz0iMDsxIiBkdXI9IjEiIGtleVNwbGluZXM9IjAgMC4yIDAuOCAxIiBiZWdpbj0iMHMiIHJlcGVhdENvdW50PSJpbmRlZmluaXRlIj48L2FuaW1hdGU+PGFuaW1hdGUgYXR0cmlidXRlTmFtZT0ib3BhY2l0eSIgY2FsY01vZGU9InNwbGluZSIgdmFsdWVzPSIxOzAiIGtleVRpbWVzPSIwOzEiIGR1cj0iMSIga2V5U3BsaW5lcz0iMC4yIDAgMC44IDEiIGJlZ2luPSIwcyIgcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiPjwvYW5pbWF0ZT48L2NpcmNsZT48L3N2Zz4=);
    z-index: 101;
  }
}

.collapse-arrow-rotated {
  transform: rotate(-90deg);
}

.back_to_cars-btn:hover .back_to_cars-icon path {
  fill: #fff;
}

.notes_gallery_collapse {
  .notes_header {
    border-radius: 0.375rem;
    cursor: pointer;

    .chevron-down {
      transition: .3s ease-out;
    }

    .chevron-down.collapsed {
      transform: rotate(180deg);
      transition: .3s ease-in;
    }
  }

  .notes_body-wrapper {
    border-bottom-right-radius: 0.375rem;
    border-bottom-left-radius: 0.375rem;
    border-top-width: 0;
  }

  .main_note {
    min-height: 100px;
  }

  .additional-notes {
    .additional_notes-item {
      min-height: 100px;
    }
    .additional_note-image {
      max-width: 64px;
    }

    .title_block-img {
      border-top-left-radius: 0.375rem;
      border-top-right-radius: 0.375rem;

      strong {
        font-weight: 600;
        font-size: 14px;
      }
    }

    .note_img-block a {
      cursor: zoom-in;
    }
  }

  .packs_gallery-notes {
    min-height: 100px;
  }

  .title_note-block {
    border-top-left-radius: 0.375rem;
    border-top-right-radius: 0.375rem;

    strong {
      white-space: nowrap;
      font-size: 14px;
      font-weight: 600;
    }
  }

  .note_block-wrapper {
    padding-top: 28px;
  }
}

.notes_gallery_collapse.collapsed {
  .notes_header {
    border-radius: 0;
    border-top-right-radius: 0.375rem;
    border-top-left-radius: 0.375rem;

    .chevron-down {
      transform: rotate(180deg);
      transition: .3s ease-in;
    }
  }
}

.photo-preview-container {
  display: block;
  cursor: zoom-in;
  border-radius: 4px;
  background-color: #fff;

  img {
    transition: transform 0.3s ease;
  }

  .hover-overlay {
    opacity: 0;
    background-color: rgba(0, 0, 0, 0.4);
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  &:hover {
    img {
      transform: scale(1.02);
    }

    .hover-overlay {
      opacity: 1;
    }
  }
}

</style>